import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Stack, Tab, Tabs, TextField, Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  DistributorSettings, fetchDistributorSettings, updateDistributorSettings,
  fetchDistributorTeam, addStaff, setStaffStatus,
} from "../../data/api";
import { useAuth } from "../../auth/AuthContext";

/** Distributor Settings (Oct-10 QA #5): profile, delivery and team management,
 *  scoped server-side to the distributor's own tenant. Staff management lives
 *  here, separate from the Customers screen. */
const SettingsPage: React.FC = () => {
  const { impersonating } = useAuth();
  const readOnly = impersonating; // view-as sessions are read-only
  const [tab, setTab] = useState(0);
  const [s, setS] = useState<DistributorSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ sev: "success" | "error"; text: string } | null>(null);

  const [team, setTeam] = useState<any[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", email: "", password: "", phone: "", role: "SELLER" });
  const [savingStaff, setSavingStaff] = useState(false);
  const [staffErr, setStaffErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setS(await fetchDistributorSettings()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    try { setTeam(await fetchDistributorTeam()); }
    finally { setTeamLoading(false); }
  }, []);
  useEffect(() => { if (tab === 2) loadTeam(); }, [tab, loadTeam]);

  const set = (patch: Partial<DistributorSettings>) => setS((cur) => ({ ...(cur || {}), ...patch }));

  const save = async () => {
    if (!s) return;
    setSaving(true); setMsg(null);
    try {
      await updateDistributorSettings({
        name: s.name, primaryContact: s.primaryContact, contactEmail: s.contactEmail,
        supportEmail: s.supportEmail, supportPhone: s.supportPhone, addressLine: s.addressLine,
        primaryColor: s.primaryColor,
        deliveryFee: s.deliveryFee ?? null, freeDeliveryThreshold: s.freeDeliveryThreshold ?? null,
        deliveryLeadTimeDays: s.deliveryLeadTimeDays ?? null, deliveryNotes: s.deliveryNotes,
      });
      setMsg({ sev: "success", text: "Settings saved." });
      await load();
    } catch (e: any) {
      setMsg({ sev: "error", text: e?.response?.data?.message || "Could not save settings." });
    } finally { setSaving(false); }
  };

  const createStaff = async () => {
    setStaffErr(null);
    if (!staffForm.email.trim() || staffForm.password.length < 8) {
      setStaffErr("Email and a password of at least 8 characters are required."); return;
    }
    setSavingStaff(true);
    try {
      await addStaff(staffForm);
      setAddOpen(false);
      setStaffForm({ name: "", email: "", password: "", phone: "", role: "SELLER" });
      await loadTeam();
    } catch (e: any) {
      setStaffErr(e?.response?.data?.message || "Could not add staff member.");
    } finally { setSavingStaff(false); }
  };

  const toggleStaff = async (id: number, status: string) => {
    try { await setStaffStatus(id, status); await loadTeam(); }
    catch (e: any) { setMsg({ sev: "error", text: e?.response?.data?.message || "Could not update staff status." }); }
  };

  if (loading) return <Box sx={{ display: "grid", placeItems: "center", py: 8 }}><CircularProgress /></Box>;

  const num = (v: any) => (v === "" || v == null ? undefined : Number(v));

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Distributor profile, delivery settings and team management for {s?.name || "your store"}.
      </Typography>

      {readOnly && <Alert severity="info" sx={{ mb: 2 }}>Read-only inspection — exit view-as to make changes.</Alert>}
      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {["Profile", "Delivery", "Team"].map((l) => <Tab key={l} label={l} />)}
      </Tabs>

      {tab === 0 && (
        <Card><CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Company name" value={s?.name || ""} onChange={(e) => set({ name: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Primary contact" value={s?.primaryContact || ""} onChange={(e) => set({ primaryContact: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Contact email" value={s?.contactEmail || ""} onChange={(e) => set({ contactEmail: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Support email" value={s?.supportEmail || ""} onChange={(e) => set({ supportEmail: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Support phone" value={s?.supportPhone || ""} onChange={(e) => set({ supportPhone: e.target.value })} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Business address" value={s?.addressLine || ""} onChange={(e) => set({ addressLine: e.target.value })} /></Grid>
          </Grid>
          <Button sx={{ mt: 2 }} variant="contained" onClick={save} disabled={saving || readOnly}>{saving ? <CircularProgress size={18} /> : "Save profile"}</Button>
        </CardContent></Card>
      )}

      {tab === 1 && (
        <Card><CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" type="number" label="Delivery fee (CAD)" value={s?.deliveryFee ?? ""} onChange={(e) => set({ deliveryFee: num(e.target.value) })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" type="number" label="Free delivery over (CAD)" value={s?.freeDeliveryThreshold ?? ""} onChange={(e) => set({ freeDeliveryThreshold: num(e.target.value) })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" type="number" label="Delivery lead time (days)" value={s?.deliveryLeadTimeDays ?? ""} onChange={(e) => set({ deliveryLeadTimeDays: num(e.target.value) })} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" multiline minRows={2} label="Delivery notes" value={s?.deliveryNotes || ""} onChange={(e) => set({ deliveryNotes: e.target.value })} /></Grid>
          </Grid>
          <Button sx={{ mt: 2 }} variant="contained" onClick={save} disabled={saving || readOnly}>{saving ? <CircularProgress size={18} /> : "Save delivery settings"}</Button>
        </CardContent></Card>
      )}

      {tab === 2 && (
        <Card><CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Team members</Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setAddOpen(true)} disabled={readOnly}>Add staff</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">Distributor administrators and staff. Customers are managed separately on the Customers screen.</Typography>
          {teamLoading ? <Box sx={{ display: "grid", placeItems: "center", py: 4 }}><CircularProgress /></Box> : (
            <Box sx={{ overflowX: "auto", mt: 1 }}>
              <Table size="small">
                <TableHead><TableRow>{["Name", "Email", "Role", "Status", "Actions"].map((h) => <TableCell key={h} sx={{ color: "text.secondary", fontWeight: 700 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {team.length === 0 ? (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No staff yet.</Typography></TableCell></TableRow>
                  ) : team.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name || "—"}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell><Chip size="small" label={u.status} color={(u.status || "").toUpperCase() === "SUSPENDED" ? "error" : "success"} /></TableCell>
                      <TableCell>
                        {(u.status || "").toUpperCase() === "SUSPENDED"
                          ? <Button size="small" disabled={readOnly} onClick={() => toggleStaff(u.id, "APPROVED")}>Reactivate</Button>
                          : <Button size="small" color="error" disabled={readOnly} onClick={() => toggleStaff(u.id, "SUSPENDED")}>Suspend</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent></Card>
      )}

      <Dialog open={addOpen} onClose={() => !savingStaff && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add staff member</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Name" size="small" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} />
            <TextField label="Email" size="small" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} required />
            <TextField label="Temporary password" size="small" type="password" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} helperText="At least 8 characters" required />
            <TextField label="Phone" size="small" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} />
            <TextField select SelectProps={{ native: true }} label="Role" size="small" value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}>
              <option value="SELLER">Staff (Seller)</option>
              <option value="DISTRIBUTOR">Distributor admin</option>
            </TextField>
            {staffErr && <Typography variant="caption" color="error">{staffErr}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={savingStaff}>Cancel</Button>
          <Button variant="contained" onClick={createStaff} disabled={savingStaff}>{savingStaff ? <CircularProgress size={18} /> : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
